import RAPIER from '@dimforge/rapier3d-compat';

import {
  BODY_BLOCK_TEAMMATES, BODY_RADIUS, COYOTE_SECONDS, type Faction,
} from '../core/constants.ts';
import type { Vec3 } from '../core/math.ts';
import { isEmbodied, playerHeight, type PlayerState } from '../core/player.ts';
import type { BodyMove } from '../core/sim.ts';
import { CHARACTER_GROUPS, MOVE_FILTER } from './levelColliders.ts';

/**
 * Every mage's capsule, and the one way a capsule is moved — for both ends.
 *
 * The server held a capsule for every mage; the client held one, for you, in
 * `FirstPersonController`, built by its own code with its own numbers. They
 * disagreed about autostep height, snap distance, slope limit and how a crouch
 * resized the body, and — the one players felt — the client had nobody else to
 * bump into. Walking into another mage stopped you on the server and not on
 * your screen, and the correction read as rubber-banding.
 *
 * Now there is one class. The server's `PhysicsWorld` and the client's
 * `GameWorld` each hold one; the room hands it the roster every tick
 * (`sync`), and core moves bodies through it (`moveBody`). On a client in a
 * hosted match only your own body is moved — everyone else's is placed where the
 * latest snapshot says, which is as close to the server's answer as the client
 * can know.
 *
 * ## Body blocking
 *
 * Mages collide side-on. Three rules keep that from turning into a toy:
 *
 * 1. **Only side-on.** Two bodies block one another only when they overlap in
 *    height by more than `HEAD_ALLOWANCE`. Land on somebody and you slide down
 *    through them to the floor; nobody can stand on a head, stack into a
 *    ladder, or be lifted by the mage underneath.
 * 2. **Never from inside, never from behind.** Bodies that already overlap —
 *    two mages spawned on one point, a Cleric resurrecting at your feet, the
 *    slide just described — ignore one another until they have separated, and a
 *    mage you are moving away from never holds you back. Without these a pair
 *    that had touched would be stuck together, because Rapier refuses to move a
 *    character in any direction from a collider it already overlaps.
 * 3. **Enemies only**, unless `BODY_BLOCK_TEAMMATES` says otherwise. Blocking a
 *    doorway against the enemy is tactics; blocking your own side's doorway is
 *    griefing, and in a three-way fight your own team is the one most often in
 *    your way.
 *
 * Ghosts and the dead neither block nor are blocked by anybody.
 *
 * ## Why not Rapier
 *
 * The first version let Rapier's character controller sweep against the other
 * capsules and decided who blocked whom with its filter callback. Rapier never
 * consults that callback for a collider the character already overlaps — it
 * just refuses to move — so the "never from inside" rule could only be had by
 * switching colliders off around every sweep and rebuilding Rapier's query
 * structures each time: 49 rebuilds a tick with 24 bots, a third of the
 * server's budget. Two upright capsules meeting side-on are two circles on the
 * floor plan, so the mages are clipped here, in plain arithmetic, and Rapier
 * sweeps what is left against the level. The level always has the last word,
 * so no clip against a mage can push anybody into a wall; and since bodies are
 * plain data here, a mage moved earlier in a tick is met where they now stand.
 */

/**
 * How much two bodies must overlap in height before they block one another.
 * Half a metre: a jump onto somebody's shoulders is a pass, a shoulder into
 * somebody's shoulder is a wall.
 */
export const HEAD_ALLOWANCE = 0.5;

/** Gap kept between two mages at rest against each other, metres. */
const BODY_SKIN = 0.01;
/** How far two mages must overlap before they count as having started inside one another. */
const INSIDE_TOLERANCE = 0.1;

/** Grounded movement. Flight turns all three off; see `moveBody`. */
const AUTOSTEP_HEIGHT = 0.45;
const AUTOSTEP_MIN_WIDTH = 0.2;
const SNAP_TO_GROUND = 0.35;
const MAX_SLOPE = (50 * Math.PI) / 180;

interface Body {
  id: string;
  rigid: RAPIER.RigidBody;
  collider: RAPIER.Collider;
  controller: RAPIER.KinematicCharacterController;
  /** Seconds since the controller last reported ground, for coyote time. */
  airborneFor: number;
  height: number;
  /** Feet, where the simulation last put this body. Read by every other move. */
  pos: Vec3;
  faction: Faction;
  /** Alive and embodied: the only bodies that block anybody. */
  solid: boolean;
  /** Which way the controller is configured, so it is only changed on a change. */
  flying: boolean | null;
}

export class CharacterBodies {
  private readonly physics: RAPIER.World;
  private readonly bodies = new Map<string, Body>();

  constructor(physics: RAPIER.World) {
    this.physics = physics;
  }

  has(id: string): boolean {
    return this.bodies.has(id);
  }

  get size(): number {
    return this.bodies.size;
  }

  /** A capsule for this mage, where the simulation says they are. */
  admit(p: PlayerState): void {
    if (this.bodies.has(p.id)) return;
    const height = playerHeight(p);
    const rigid = this.physics.createRigidBody(
      RAPIER.RigidBodyDesc.kinematicPositionBased()
        .setTranslation(p.pos.x, p.pos.y + height / 2, p.pos.z),
    );
    const collider = this.physics.createCollider(
      RAPIER.ColliderDesc.capsule(height / 2 - BODY_RADIUS, BODY_RADIUS)
        .setCollisionGroups(CHARACTER_GROUPS),
      rigid,
    );
    const controller = this.physics.createCharacterController(0.02);
    controller.setApplyImpulsesToDynamicBodies(false);

    const body: Body = {
      id: p.id, rigid, collider, controller, airborneFor: 0, height, pos: { ...p.pos },
      faction: p.faction, solid: isEmbodied(p) && !p.ghost, flying: null,
    };
    this.configure(body, p.flying || p.ghost);
    this.bodies.set(p.id, body);
  }

  release(id: string): void {
    const body = this.bodies.get(id);
    if (!body) return;
    this.physics.removeCharacterController(body.controller);
    this.physics.removeRigidBody(body.rigid);
    this.bodies.delete(id);
  }

  /**
   * Brings the bodies into line with a roster: a capsule for everybody listed,
   * none for anybody who is not, each placed where the roster says and told
   * whether it is solid.
   *
   * The room calls this before every tick; a client in a hosted match calls it
   * every frame with the latest snapshot. Cheap when nothing changed: placing a
   * kinematic body is a write, not a query.
   */
  sync(players: readonly PlayerState[]): void {
    const live = new Set<string>();
    for (const p of players) {
      live.add(p.id);
      if (!this.bodies.has(p.id)) this.admit(p);
      const body = this.bodies.get(p.id)!;
      body.faction = p.faction;
      body.solid = isEmbodied(p) && !p.ghost;
      this.fitStance(p, body);
      this.put(body, p.pos);
    }
    for (const id of [...this.bodies.keys()]) if (!live.has(id)) this.release(id);
  }

  /** Puts a body exactly where the simulation says, after a teleport. */
  place(p: PlayerState): void {
    const body = this.bodies.get(p.id);
    if (!body) return;
    this.put(body, p.pos);
  }

  /**
   * The `WorldQuery.moveBody` contract: core decides where somebody is *trying*
   * to go, and this says where they got.
   *
   * Placed from the simulation first, then swept. Reading the body's own
   * translation instead reads where Rapier last *stepped* it to — once a frame
   * on a client, while the simulation ticks at sixty hertz — so below sixty
   * frames a second every tick after the first in a frame started from the
   * wrong place.
   */
  moveBody(p: PlayerState, delta: Vec3, dt: number): BodyMove | null {
    const body = this.bodies.get(p.id);
    if (!body) return null;

    body.faction = p.faction;
    body.solid = isEmbodied(p) && !p.ghost;
    this.fitStance(p, body);
    this.configure(body, p.flying || p.ghost);

    const half = body.height / 2;
    const from = { x: p.pos.x, y: p.pos.y + half, z: p.pos.z };
    body.collider.setTranslation(from);
    body.rigid.setTranslation(from, true);

    // Other mages first, in arithmetic; then Rapier, against the level only.
    const clipped = this.clipAgainstBodies(body, p.pos, delta);
    body.controller.computeColliderMovement(body.collider, clipped, undefined, MOVE_FILTER);

    const moved = body.controller.computedMovement();
    const next = { x: from.x + moved.x, y: from.y + moved.y, z: from.z + moved.z };
    body.collider.setTranslation(next);
    body.rigid.setTranslation(next, true);
    body.pos = { x: next.x, y: next.y - half, z: next.z };

    /*
     * Debounced before anything else sees it. One bad frame on a step is not
     * leaving the ground, and this doubles as the coyote time a first-person
     * game wants for jumping off a ledge.
     */
    const grounded = body.controller.computedGrounded();
    body.airborneFor = grounded ? 0 : body.airborneFor + dt;

    return {
      pos: { x: next.x, y: next.y - half, z: next.z },
      grounded: grounded || body.airborneFor < COYOTE_SECONDS,
    };
  }

  /**
   * Whether another mage can stop this one at all — the side, the life and the
   * height rules from the header. Direction is the clip's business.
   */
  private canBlock(mover: Body, feet: number, other: Body): boolean {
    if (other === mover || !mover.solid || !other.solid) return false;
    if (!BODY_BLOCK_TEAMMATES && other.faction === mover.faction) return false;
    const overlap = Math.min(feet + mover.height, other.pos.y + other.height)
      - Math.max(feet, other.pos.y);
    return overlap >= HEAD_ALLOWANCE;
  }

  /**
   * The horizontal part of a move, cut short where it would walk into another
   * mage, with whatever is left slid along them. Two upright capsules meeting
   * side-on are two circles on the floor plan, so this is circle against circle:
   * find the first mage the move would touch, stop there, turn the remainder
   * tangent to them, and look again — at most three times, which covers a mage
   * sliding round one body into the next.
   *
   * Only a mage being moved *toward* can stop you, and a mage you already
   * overlap cannot — so a pair who spawned on one point, or who slid down
   * through one another, simply walk apart.
   */
  private clipAgainstBodies(body: Body, from: Vec3, delta: Vec3): Vec3 {
    // Contact is a hair outside the bodies, so floating-point drift and the
    // level sweep's own nudges never carry two touching mages inside each other;
    // "inside" is well inside, so a mage resting against another is not mistaken
    // for one that started in them.
    const reach = BODY_RADIUS * 2 + BODY_SKIN;
    const inside = BODY_RADIUS * 2 - INSIDE_TOLERANCE;
    let x = from.x;
    let z = from.z;
    let dx = delta.x;
    let dz = delta.z;

    const blockers: Body[] = [];
    for (const other of this.bodies.values()) {
      if (this.canBlock(body, from.y, other)) blockers.push(other);
    }
    if (!blockers.length) return delta;

    for (let pass = 0; pass < 3 && (dx !== 0 || dz !== 0); pass++) {
      let first = 1;
      let hit: Body | null = null;
      for (const other of blockers) {
        const ox = x - other.pos.x;
        const oz = z - other.pos.z;
        const d2 = ox * ox + oz * oz;
        if (d2 < inside * inside) continue; // already inside: never from inside
        const c = Math.max(0, d2 - reach * reach);
        const b = ox * dx + oz * dz;
        if (b >= 0) continue; // not moving toward them
        const a = dx * dx + dz * dz;
        const disc = b * b - a * c;
        if (disc < 0) continue; // passes by
        const t = Math.max(0, (-b - Math.sqrt(disc)) / a);
        if (t < first) { first = t; hit = other; }
      }
      if (!hit) break;

      // Up to the contact, then the remainder with the inward part removed.
      x += dx * first;
      z += dz * first;
      let rx = dx * (1 - first);
      let rz = dz * (1 - first);
      const nx = x - hit.pos.x;
      const nz = z - hit.pos.z;
      const len = Math.hypot(nx, nz) || 1;
      const into = (rx * nx + rz * nz) / len;
      if (into < 0) {
        rx -= (into * nx) / len;
        rz -= (into * nz) / len;
      }
      dx = rx;
      dz = rz;
    }
    return { x: x + dx - from.x, y: delta.y, z: z + dz - from.z };
  }

  private put(body: Body, feet: Vec3): void {
    body.pos = { x: feet.x, y: feet.y, z: feet.z };
    const at = { x: feet.x, y: feet.y + body.height / 2, z: feet.z };
    body.rigid.setTranslation(at, true);
    body.collider.setTranslation(at);
  }

  /** Capsule height follows the simulation's stance, feet staying where they are. */
  private fitStance(p: PlayerState, body: Body): void {
    const wanted = playerHeight(p);
    if (Math.abs(wanted - body.height) < 0.01) return;
    body.collider.setHalfHeight(Math.max(0.01, wanted / 2 - BODY_RADIUS));
    body.height = wanted;
    this.put(body, p.pos);
  }

  /**
   * Grounded or flying. A flying body gets no autostep, no snap to the ground and
   * no slope limit: autostep on a hovering capsule grabs at every ledge it
   * passes and lifts a ghost onto it, and snap-to-ground pulls a hovering
   * Mentalist down onto the floor beneath.
   */
  private configure(body: Body, flying: boolean): void {
    if (body.flying === flying) return;
    body.flying = flying;
    if (flying) {
      body.controller.disableAutostep();
      body.controller.disableSnapToGround();
      body.controller.setMaxSlopeClimbAngle(Math.PI / 2);
    } else {
      body.controller.enableAutostep(AUTOSTEP_HEIGHT, AUTOSTEP_MIN_WIDTH, true);
      body.controller.enableSnapToGround(SNAP_TO_GROUND);
      body.controller.setMaxSlopeClimbAngle(MAX_SLOPE);
    }
  }
}
